import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import adminApiClient from '@/services/adminApiClient';

const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳' }, { code: '+1', flag: '🇺🇸' }, { code: '+44', flag: '🇬🇧' },
  { code: '+61', flag: '🇦🇺' }, { code: '+971', flag: '🇦🇪' }, { code: '+65', flag: '🇸🇬' },
];

export default function Login() {
  const [tab, setTab] = useState<'password' | 'otp'>('password');

  // Password login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // OTP login state
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState<'phone' | 'otp'>('phone');
  const [cooldown, setCooldown] = useState(0);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const switchTab = (t: 'password' | 'otp') => {
    setTab(t); setError('');
    setOtpStep('phone'); setOtp(''); setPhone('');
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await adminApiClient.post('/login', { email, password });
      setAuth(res.data.token, res.data.admin);
      navigate('/');
    } catch {
      setError('Invalid credentials or insufficient permissions.');
    } finally { setLoading(false); }
  };

  const fullPhone = `${countryCode}${phone.replace(/\D/g, '')}`;

  const handleSendOtp = async () => {
    setError(''); setLoading(true);
    try {
      await adminApiClient.post('/otp/send', { phone: fullPhone });
      setOtpStep('otp'); setCooldown(30);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Failed to send OTP.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    setError(''); setLoading(true);
    try {
      const res = await adminApiClient.post('/otp/verify', { phone: fullPhone, otp });
      setAuth(res.data.token, res.data.admin);
      navigate('/');
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Invalid or expired OTP.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-admin-bg flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-sm border border-admin-border p-8 w-full max-w-sm">
        <h1 className="text-xl font-semibold text-slate-800 mb-6">Admin Login</h1>

        {/* Tab toggle */}
        <div className="flex rounded-lg border border-admin-border mb-6 overflow-hidden">
          <button
            onClick={() => switchTab('password')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${tab === 'password' ? 'bg-admin-accent text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            Email & Password
          </button>
          <button
            onClick={() => switchTab('otp')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${tab === 'otp' ? 'bg-admin-accent text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            Phone OTP
          </button>
        </div>

        {tab === 'password' ? (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
              />
            </div>
            {error && <p className="text-sm text-admin-danger">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-admin-accent hover:bg-admin-accent-hover text-white font-medium py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            {otpStep === 'phone' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone number</label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      className="px-3 py-2 border border-admin-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent bg-white"
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="98765 43210"
                      className="flex-1 border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-admin-danger">{error}</p>}
                <button
                  onClick={handleSendOtp}
                  disabled={loading || !phone.trim()}
                  className="w-full bg-admin-accent hover:bg-admin-accent-hover text-white font-medium py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    OTP sent to {countryCode} {phone}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="_ _ _ _"
                    autoFocus
                    className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent tracking-widest text-center text-lg font-mono"
                  />
                </div>
                {error && <p className="text-sm text-admin-danger">{error}</p>}
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length < 4}
                  className="w-full bg-admin-accent hover:bg-admin-accent-hover text-white font-medium py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
                >
                  {loading ? 'Verifying...' : 'Verify & Sign in'}
                </button>
                <button
                  onClick={handleSendOtp}
                  disabled={cooldown > 0 || loading}
                  className={`w-full text-sm text-center py-1 ${cooldown > 0 ? 'text-slate-400' : 'text-admin-accent hover:underline'}`}
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                </button>
                <button
                  onClick={() => { setOtpStep('phone'); setOtp(''); setError(''); }}
                  className="w-full text-sm text-slate-400 hover:text-slate-600 text-center"
                >
                  Change number
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
